import { OfferType } from 'src/common/apollo/types/swarmx/graphql'
import { Offer } from 'src/common/types/models/Offer'
import Big from 'big.js'
import { calcAvgOffersPrice } from './offerPrices'

export interface OfferWithTake extends Offer {
  toTake: Big
  isFullyAuthorized?: boolean | null
}

export interface OfferWithAvailableOut extends Offer {
  availableAmountOut: Big
}

enum FindTargetMode {
  Out,
  In,
}

function findUniqueOffersCombinations(
  offers: OfferWithAvailableOut[],
  target: Big,
  mode: FindTargetMode = 0
): OfferWithTake[][] {
  const limit = offers.length > 10 ? 2 : 8
  const result: OfferWithTake[][] = []
  const offersWithTake = offers.map<OfferWithTake>(offer => ({
    ...offer,
    toTake: mode === 0 ? offer.availableAmountOut : offer.availableAmount,
  }))

  function backtrack(
    start: number,
    currentCombo: OfferWithTake[],
    remainingTarget: Big,
    usedPartials: OfferWithTake[]
  ) {
    if (remainingTarget.eq(Big(0))) {
      result.push([...currentCombo])
      return
    }

    for (let i = start; i < offersWithTake.length; i++) {
      if (result.length >= limit) break
      const currentObject = offersWithTake[i]
      const currentValue = currentObject.toTake
      const isPartial = currentObject.offerType === OfferType.PartialOffer

      if (isPartial) {
        if (currentValue.gte(remainingTarget)) {
          // Use just the portion needed to reach the target
          currentCombo.push({ ...currentObject, toTake: remainingTarget })
          result.push([...currentCombo])
          currentCombo.pop()
        } else {
          // Use the full partial value and add it to used partials
          currentCombo.push(currentObject)
          usedPartials.push(currentObject)
          backtrack(
            i + 1,
            currentCombo,
            remainingTarget.minus(currentValue),
            usedPartials
          )
          currentCombo.pop()
          usedPartials.pop()
        }
      } else {
        if (currentValue.lte(remainingTarget)) {
          currentCombo.push(currentObject)
          backtrack(
            i + 1,
            currentCombo,
            remainingTarget.minus(currentValue),
            usedPartials
          )
          currentCombo.pop()
        } else {
          // Try to adjust with used partials to fit the target
          for (let j = 0; j < usedPartials.length; j++) {
            const partial = usedPartials[j]
            const adjustment = currentValue.minus(remainingTarget)
            if (partial.toTake.gt(adjustment)) {
              // Adjust partial value to reach the target
              partial.toTake = partial.toTake.minus(adjustment)
              currentCombo.push(currentObject)
              result.push([...currentCombo])
              currentCombo.pop()
              break
            }
          }
        }
      }
    }
  }

  backtrack(0, [], target, [])
  return result
}

function normalizeToTake(
  offers: OfferWithTake[],
  mode: FindTargetMode
): OfferWithTake[] {
  if (mode === 0) return offers
  return offers.map(offer => ({
    ...offer,
    toTake: offer.toTake.mul(offer.amountOut!.div(offer.amountIn)),
  }))
}

export function findBestOffers(
  offers: OfferWithAvailableOut[],
  target: Big,
  mode: FindTargetMode = 0
): OfferWithTake[] {
  if (offers.length === 0 || target.eq(Big(0))) return []
  const result = findUniqueOffersCombinations(offers, target, mode)
  const bestPriceOffers = result.reduce<[Big, OfferWithTake[]]>(
    (acc, offers) => {
      const avgPrice = calcAvgOffersPrice(offers)
      if (avgPrice.gt(acc[0])) {
        return [avgPrice, offers]
      }
      return acc
    },
    [calcAvgOffersPrice(result[0]), result[0]]
  )
  return normalizeToTake(bestPriceOffers[1] || [], mode)
}

/*

  Sell 1 USDC

  For each offer calc available amount of USDC according to exchange price inside the offer
  Calc best sequence of offers to match USDC according to available amount






*/
